import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { catchError, filter, map, of, take, timeout } from 'rxjs';
import { ConfigService } from './config.service';

// How long the dashboard waits for GET /config before rendering anyway.
export const CONFIG_READY_TIMEOUT_MS = 4000;

/**
 * Holds activation of the dashboard shell until the authenticated config is in hand.
 *
 * `GET /public-config` omits every module feature flag (it carries only the login-page subset),
 * so a page that reads its flag off that response sees `undefined` - which switcher services
 * turn into 'mock'. Because they resolve the flag with `take(1)`, whichever config emission wins
 * the race sticks for the life of that request, which is why pages rendered mock or empty data
 * and then came right after a refresh.
 *
 * Guarding the shell route fixes every child page at once, rather than each of the ~60 switcher
 * services needing to remember the same `filter(cfg => cfg.authenticated === true)`. The fetch is
 * triggered here rather than in DashboardComponent because guards run before the shell component
 * exists - waiting on a fetch the shell has not made yet would deadlock. Triggering it here also
 * unblocks it from `whoAmI()`, which it used to be chained behind.
 *
 * Fails open: after CONFIG_READY_TIMEOUT_MS the route activates regardless. A logged-in user
 * bounced to the login page would just be sent back here by `authGuard`, so blocking on a slow
 * backend risks a redirect loop, and stranding them on a blank shell is worse than the stale-flag
 * render this used to produce on every load.
 */
export const configReadyGuard: CanActivateFn = () => {
  const configService = inject(ConfigService);
  configService.loadFullConfig();

  return configService.getConfig().pipe(
    filter((cfg) => cfg.authenticated === true),
    take(1),
    map(() => true),
    timeout({ first: CONFIG_READY_TIMEOUT_MS, with: () => of(true) }),
    catchError(() => of(true)),
  );
};
